import * as Sentry from '@sentry/react';

import { useCurrentAsset } from '@/hooks/use-current-asset';
import { formatDate } from '@/utils/format/date';
import { useCurrentSystemId } from '@/warranty-v3/hooks/use-current-system-id';
import { GetMetricsQuery } from '@/warranty-v3/queries/get-metrics';
import { orderElements } from '@/warranty-v3/utils/order-elements';

import { MetricChart } from './metric-chart';
import { MetricsGroupBox } from './metrics-group-box';
import { MetricsLoading } from './metrics-loading';

export function Metrics() {
  const systemId = useCurrentSystemId();
  const metricsQuery = GetMetricsQuery.useQuery({ systemId });
  const { asset } = useCurrentAsset();

  if (metricsQuery.isLoading) {
    return <MetricsLoading />;
  }

  if (metricsQuery.isError && !metricsQuery.data) {
    Sentry.captureException(metricsQuery.error);
    // If an error happened, we prefer to hide the metrics
    return null;
  }

  const timeBasedMetrics = metricsQuery.data;

  const groups = orderElements(timeBasedMetrics.groups);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 rounded-lg border-2 border-gray-200 bg-gray-50 p-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          {asset?.fullFrontendId ?? null} Performance Metrics
        </h2>
        <p className="text-sm text-gray-700">
          KPI data filtered by selected time period
        </p>
        <div className="mt-2 text-xs text-gray-600">
          Current Warranty Period: {formatDate(timeBasedMetrics.start)} →{' '}
          {formatDate(timeBasedMetrics.end)}
        </div>
      </div>
      <div className="grid w-full auto-cols-fr grid-flow-col gap-6">
        {groups.map((group) => (
          <MetricsGroupBox key={group.id} group={group} />
        ))}
      </div>
      <MetricChart timeBasedMetrics={timeBasedMetrics} />
    </div>
  );
}
